// API utility functions that work with both backends
import { authHelpers } from '../supabaseClient'
import { API_BASE_URL } from '../config'

class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL
  }

  async getAuthHeaders() {
    try {
      const { data: { session } } = await authHelpers.getSession()
      console.log('🔑 AUTH HEADERS DEBUG: Session retrieved:', !!session);
      console.log('🔑 AUTH HEADERS DEBUG: Access token exists:', !!session?.access_token);
      console.log('🔑 AUTH HEADERS DEBUG: User ID:', session?.user?.id);
      console.log('🔑 AUTH HEADERS DEBUG: User email:', session?.user?.email);
      
      if (session?.access_token) {
        console.log('🔑 AUTH HEADERS DEBUG: Using auth headers with token length:', session.access_token.length);
        return {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    } catch (error) {
      console.error('❌ AUTH HEADERS DEBUG: Error getting session:', error)
    }
    
    console.log('🔓 AUTH HEADERS DEBUG: Using no auth headers - no valid session');
    return {
      'Content-Type': 'application/json'
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`
    const headers = await this.getAuthHeaders()
    
    console.log(`🌐 API REQUEST DEBUG: Making request to: ${url}`);
    console.log(`🌐 API REQUEST DEBUG: Headers:`, headers);
    console.log(`🌐 API REQUEST DEBUG: Options:`, options);
    
    const config = {
      headers,
      ...options
    }

    try {
      console.log(`📡 API REQUEST DEBUG: Sending fetch request with config:`, config);
      const response = await fetch(url, config)
      console.log(`📡 API RESPONSE DEBUG: Response status: ${response.status}`);
      console.log(`📡 API RESPONSE DEBUG: Response headers:`, Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        console.error(`❌ API ERROR DEBUG: Response not OK, status: ${response.status}`);
        const errorData = await response.json().catch(() => ({}))
        console.error('❌ API ERROR DEBUG: Error data:', errorData);
        throw new Error(errorData.detail || `HTTP ${response.status}`)
      }

      // Capture raw response for debugging
      const rawResponse = await response.text()
      console.log('📦 RAW RESPONSE DEBUG: Response text length:', rawResponse.length);
      console.log('📦 RAW RESPONSE DEBUG: First 500 chars:', rawResponse.substring(0, 500));
      console.log('📦 RAW RESPONSE DEBUG: Content-Type:', response.headers.get('content-type'));

      // Defensive content-type checking
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        console.error('❌ CONTENT-TYPE DEBUG: Expected JSON but got:', contentType);
        console.error('❌ CONTENT-TYPE DEBUG: Response appears to be HTML/text:', rawResponse.slice(0, 300));
        throw new Error(`Expected JSON response but got ${contentType}. This usually means the API endpoint wasn't found and returned HTML instead.`);
      }

      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(rawResponse);
        console.log('✅ JSON PARSE DEBUG: Successfully parsed JSON');
        console.log('✅ API SUCCESS DEBUG: Response data:', data);
      } catch (parseError) {
        console.error('❌ JSON PARSE DEBUG: Failed to parse as JSON:', parseError);
        console.error('❌ JSON PARSE DEBUG: Raw response was:', rawResponse);
        throw new Error(`JSON Parse Error: ${parseError.message}. Raw response: ${rawResponse.substring(0, 200)}...`);
      }

      return data
    } catch (error) {
      console.error(`❌ API REQUEST DEBUG: Request failed for ${endpoint}:`, error)
      console.error(`❌ API REQUEST DEBUG: Error type:`, error.constructor.name)
      console.error(`❌ API REQUEST DEBUG: Error message:`, error.message)
      throw error
    }
  }

  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' })
  }

  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' })
  }

  // v2 Catalog API (replaces getNarratives for the landing page)
  async getCatalog() {
    return this.get('/catalog')
  }

  // Narrative API methods
  async getNarratives(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`/narratives/?${searchParams}`)
  }

  async getNarrativesWithStats(params = {}) {
    const searchParams = new URLSearchParams(params)
    return this.get(`/narratives/with-stats?${searchParams}`)
  }

  async getNarrative(id) {
    return this.get(`/narratives/${id}`)
  }

  async createNarrative(narrative) {
    return this.post('/narratives/', narrative)
  }

  async searchNarratives(query, genre = '', limit = 20) {
    const params = { q: query, genre, limit }
    const searchParams = new URLSearchParams(params)
    return this.get(`/narratives/search?${searchParams}`)
  }

  // Character API methods
  async getCharacters() {
    return this.get('/characters/')
  }

  // Room API methods
  async getUserRooms() {
    return this.get('/rooms/')
  }

  async getUserRoomsForNarrative(narrativeId) {
    return this.get(`/rooms/for-narrative/${narrativeId}`)
  }

  async getRoomInviteLink(roomId) {
    return this.get(`/rooms/${roomId}/invite-link`)
  }

  async getGuestRoomsForNarrative(narrativeId, guestUserId) {
    const params = new URLSearchParams({ guest_user_id: guestUserId })
    return this.get(`/rooms/guest/for-narrative/${narrativeId}?${params}`)
  }

  async createRoom(roomData) {
    return this.post('/rooms/', roomData)
  }

  async createGuestRoom(roomData) {
    return this.post('/rooms/guest', roomData)
  }

  // UNIFIED GUEST CREATION - Single entry point for all guest scenarios
  async createOrGetGuestUser(requestData = {}) {
    console.log('🎭 API: Creating or getting unified guest user:', requestData);
    return this.post('/guests/create-or-get', requestData)
  }

  // UNIFIED GUEST ROOM CREATION - Create room with unified guest
  async createGuestRoomUnified(guestId, roomData) {
    console.log('🏠 API: Creating room for unified guest:', guestId, roomData);
    return this.post(`/guests/${guestId}/rooms`, roomData)
  }

  // GUEST VALIDATION - Validate existing guest user
  async validateGuestUser(guestId) {
    console.log('🔍 API: Validating guest user:', guestId);
    return this.get(`/guests/${guestId}/validate`)
  }

  async joinRoom(roomData) {
    return this.post('/rooms/join', roomData)
  }


  async deleteRoom(roomId, guestId = null) {
    if (!roomId) {
      throw new Error('Room ID is required');
    }
    if (guestId) {
      console.log('🗑️ API: Deleting guest room:', roomId, 'for guest:', guestId);
      return this.delete(`/guests/${guestId}/rooms/${roomId}`)
    }
    console.log('🗑️ API: Deleting room:', roomId);
    return this.delete(`/${roomId}`)
  }

  async getRoomParticipants(roomId) {
    if (!roomId) {
      throw new Error('Room ID is required');
    }
    return this.get(`/${roomId}/participants`)
  }

  // Message API methods
  async getRoomMessages(roomId, limit = 50) {
    if (!roomId) {
      throw new Error('Room ID is required');
    }
    return this.get(`/messages/${roomId}?limit=${limit}`)
  }

  async createMessage(message) {
    if (!message || !message.content) {
      throw new Error('Message content is required');
    }
    return this.post('/messages/', message)
  }

  // Presence API methods
  async updatePresence(presenceData) {
    if (!presenceData) {
      throw new Error('Presence data is required');
    }
    return this.post('/presence/', presenceData)
  }

  async getRoomPresence(roomId) {
    if (!roomId) {
      throw new Error('Room ID is required');
    }
    return this.get(`/presence/${roomId}`)
  }

  // Scene state API methods
  async getSceneState(roomId) {
    if (!roomId) {
      throw new Error('Room ID is required');
    }
    return this.get(`/scene-state/${roomId}`)
  }

  async updateSceneState(sceneData) {
    if (!sceneData) {
      throw new Error('Scene data is required');
    }
    return this.post('/scene-state/', sceneData)
  }

  // Stats API methods
  async getStats() {
    return this.get('/stats')
  }

  // Auth API methods
  async getCurrentUser() {
    return this.get('/auth/me')
  }

  // Health check
  async healthCheck() {
    return this.get('/health')
  }
}

export const api = new ApiClient()
export default api
